import { Button, Progress } from 'antd';
import dayjs from 'dayjs';
import { Flag } from '../Flag';
import { Checkout } from '@src/pages/Dashboard/Checkout';
import { useMemo, useState } from 'react';
import { StyledProgressWrapper } from './styles';
import { useSelector } from 'react-redux';
import { selectLayout } from '@src/store/layout/layoutSlice';
import { cleanNumberDisplay } from '@src/utilities/currency';
import { QRCodeView, TopUpView } from '@src/pages/Dashboard/MyESims/components';
import { Esim } from '@src/services/esim/esimApiSliceTypes';

interface EismItemProps {
  esim: Esim;
  mode?: 'dashboard' | 'current' | 'history';
  packageIsAvailable?: boolean;
  isLoading?: boolean;
}

export function EismItem({
  esim,
  mode,
  packageIsAvailable = false,
}: Readonly<EismItemProps>) {
  const layout = useSelector(selectLayout);
  const [openPay, setOpenPay] = useState(false);
  const [openQR, setOpenQR] = useState(false);
  const [openTopUp, setOpenTopUp] = useState(false);

  const expiry = dayjs(esim.expiryDate);

  const remainingDataPercent = useMemo(() => {
    return Math.min(
      Math.round((esim.remainingData / esim.totalData) * 100),
      100
    );
  }, [esim.usedData, esim.totalData]);

  const badgeColors = {
    'In Use': 'bg-badge-background-inUse text-badge-text-inUse',
    Internet: 'bg-badge-background-internet text-badge-text-internet',
    eSIMReady: 'bg-badge-background-eSIMReady text-badge-text-eSIMReady',
    eSIMNotReady:
      'bg-badge-background-eSIMNotReady text-badge-text-eSIMNotReady',
  };

  const expiredClass = dayjs(esim.expiryDate).isAfter(dayjs())
    ? ''
    : 'grayscale';

  const handleCloseQr = () => {
    setTimeout(() => {
      setOpenQR(false);
    }, 300);
  };
  const handleCloseTopUp = () => {
    setTimeout(() => {
      setOpenTopUp(false);
    }, 300);
  };

  return (
    <div
      className={`h-full p-4 rounded-2xl shadow-sm bg-wl-background text-wl-on-background space-y-4 [&_hr]:opacity-40`}
    >
      {mode !== 'dashboard' && (
        <div className='text-M-20'>{esim.countryName}</div>
      )}
      <div
        className={`flex items-center justify-between gap-4 ${expiredClass}`}
      >
        <div className='flex flex-col space-y-4'>
          <div className='flex space-x-2'>
            <Flag flagSrc={esim.countryFlag} className='w-[38px] h-[38px]' />
            <div>
              <div className='text-B-16 text-wl-on-background'>
                {esim.packageName}
              </div>
              <div className='text-R-14 text-gray-400'>
                {esim.planType} plan
              </div>
            </div>
          </div>
          <div className='flex gap-2'>
            {esim.badges.map((badge) => (
              <span
                key={badge}
                className={`p-1 text-xs font-bold rounded-md ${badgeColors[badge]}`}
              >
                {badge}
              </span>
            ))}
          </div>
        </div>
        <StyledProgressWrapper theme={layout.theme}>
          <Progress
            type='circle'
            percent={remainingDataPercent}
            size={100}
            strokeWidth={12}
            strokeColor={
              mode === 'current' || mode === 'dashboard' ? '#FD5B2C' : '#EBEBEB'
            }
            format={() => (
              <>
                <span className='text-base font-bold text-gray-600 dark:text-gray-300'>
                  {cleanNumberDisplay(esim.remainingData)}
                  <span className='text-[10px] pl-1'>
                    {esim.remainingDataUnitType}
                  </span>
                </span>
                <br />
                <span className='text-[10px] text-gray-400 dark:text-gray-100'>
                  / {cleanNumberDisplay(esim.totalData)}{' '}
                  {esim.totalDataUnitType}
                </span>
              </>
            )}
          />
        </StyledProgressWrapper>
      </div>
      <hr />
      {mode != 'dashboard' && esim.topUpData !== undefined && (
        <>
          <div className='flex justify-between text-wl-on-background opacity-70'>
            <span>Top-Up</span>
            <span>
              {esim.topUpData
                ? `${esim.topUpData} ${esim.topUpDataUnitType}`
                : '-'}
            </span>
          </div>
          <hr />
        </>
      )}
      {mode === 'history' ? (
        <>
          <div className='text-sm text-gray-600'>
            From {dayjs(esim.startDate).format('YYYY MMMM D')} to{' '}
            {expiry.format('YYYY MMMM D')}
          </div>
          <hr />
          <Button
            className={`w-full py-2 text-sm font-semibold rounded-lg ${
              packageIsAvailable
                ? 'btn-primary'
                : '!bg-gray-500 btn-secondary hover:!bg-gray-500'
            }`}
            disabled={!packageIsAvailable}
            onClick={() => {
              if (packageIsAvailable) {
                setOpenPay(true);
              }
            }}
          >
            {packageIsAvailable ? 'Proceed to Purchase' : 'Not Available'}
          </Button>
        </>
      ) : (
        <>
          <div className='text-R-14 text-wl-on-background opacity-70'>
            Active until {expiry.format('DD MMMM YYYY')} ({esim.remainingDays}{' '}
            {esim.remainingDays === 1 ? 'day' : 'days'})
          </div>
          {mode != 'dashboard' && (
            <>
              <hr />
              <div className='flex justify-between gap-4 my-4'>
                <Button
                  className='btn-secondary w-1/2 !border-neutral-300'
                  variant='outlined'
                  onClick={() => {
                    setOpenQR(true);
                  }}
                >
                  QR Code
                </Button>
                <Button
                  className='btn-primary w-1/2'
                  onClick={() => {
                    setOpenTopUp(true);
                  }}
                >
                  Top-Up
                </Button>
              </div>
            </>
          )}
        </>
      )}
      {openPay && (
        <Checkout
          isOpen={openPay}
          country={{
            id: esim.countryId,
            flag: esim.countryFlag,
          }}
          packageId={esim.packageId}
          onClose={() => setOpenPay(false)}
        />
      )}
      <QRCodeView
        isOpen={openQR}
        esimId={esim.esimId}
        packageId={esim.packageId}
        onClose={handleCloseQr}
      />

      <TopUpView
        isOpen={openTopUp}
        esimId={esim.esimId}
        packageId={esim.packageId}
        flag={esim.countryFlag}
        countryName={esim.countryName}
        onClose={handleCloseTopUp}
      />
    </div>
  );
}
